import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star, ShieldCheck } from "lucide-react";
import { TESTIMONIALS } from "../data/staticData";

export default function TestimonialsCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!emblaApi) return;
    const id = setInterval(() => emblaApi.scrollNext(), 5000);
    return () => clearInterval(id);
  }, [emblaApi]);

  return (
    <div className="relative" data-testid="testimonials-carousel">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.quote}
              data-testid={`testimonial-${i}`}
              className="tactical-card p-7 md:p-8 flex-shrink-0 basis-full md:basis-[calc(50%-12px)] lg:basis-[calc(33.333%-16px)] flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }, (_, k) => `s${k}`).map((sk) => (
                    <Star
                      key={sk}
                      className="w-3.5 h-3.5 fill-[#7CFF00] text-[#7CFF00]"
                    />
                  ))}
                </div>
                <span className="font-mono-tactical text-[10px] text-[#7CFF00] tracking-widest uppercase flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              </div>
              <div className="text-[#7CFF00] text-4xl font-tactical leading-none mb-1">
                &ldquo;
              </div>
              <p className="text-zinc-300 leading-relaxed text-base flex-1">
                {t.quote}
              </p>
              <div className="mt-5 pt-4 border-t border-white/10">
                <div className="overline text-[10px]">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="mt-8 flex items-center justify-between">
        <div className="flex gap-1.5">
          {TESTIMONIALS.map((t, i) => (
            <button
              key={t.quote}
              onClick={() => emblaApi?.scrollTo(i)}
              data-testid={`testimonials-dot-${i}`}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-1 transition-all duration-300 ${
                i === selectedIndex
                  ? "w-8 bg-[#7CFF00]"
                  : "w-4 bg-white/15 hover:bg-white/30"
              }`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={scrollPrev}
            data-testid="testimonials-prev"
            aria-label="Previous"
            className="w-10 h-10 border border-white/15 hover:border-[#7CFF00] hover:text-[#7CFF00] text-white flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={scrollNext}
            data-testid="testimonials-next"
            aria-label="Next"
            className="w-10 h-10 border border-white/15 hover:border-[#7CFF00] hover:text-[#7CFF00] text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
